// Sentry initialization — must be imported FIRST in main.tsx so it boots
// before React mounts. The custom ErrorBoundary at components/ErrorBoundary.tsx
// forwards render exceptions to Sentry.captureException; this file wires the
// runtime SDK (browser tracing, session replay, console breadcrumbs).

import * as Sentry from '@sentry/react';
import type { ErrorEvent } from '@sentry/react';

// Bearer tokens, JWTs, Supabase publishable/secret keys, Stripe sk_/pk_ keys.
// Exported only for testability — same regex is applied to message + exception bodies.
export const SENTRY_SECRET_RE = /(eyJ[A-Za-z0-9._-]{20,}|sb_(?:publishable|secret)_[A-Za-z0-9_-]+|sk_(?:live|test)_[A-Za-z0-9]+|Bearer\s+[A-Za-z0-9._-]+)/g;

// PII / secret scrubber for Sentry events (RISK-002 / CV-638). Run as beforeSend.
// - Strips user identifiers Sentry would otherwise carry (sendDefaultPii=false is
//   the first line of defence; this is the second so a manual setUser() call still
//   doesn't leak email/IP).
// - Strips request cookies + Authorization headers if a helper attached them.
// - Redacts token-shaped strings in message + exception bodies.
export function scrubSentryEvent<E extends ErrorEvent>(event: E): E {
  if (event.user) {
    delete event.user.email;
    delete event.user.ip_address;
    delete event.user.username;
  }
  if (event.request) {
    delete event.request.cookies;
    if (event.request.headers) {
      const headers = event.request.headers as Record<string, string>;
      delete headers.cookie;
      delete headers.Cookie;
      delete headers.authorization;
      delete headers.Authorization;
    }
  }
  if (event.message) {
    event.message = event.message.replace(SENTRY_SECRET_RE, '[REDACTED]');
  }
  if (event.exception?.values) {
    for (const ex of event.exception.values) {
      if (ex.value) ex.value = ex.value.replace(SENTRY_SECRET_RE, '[REDACTED]');
    }
  }
  return event;
}

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

if (dsn) {
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Privacy defaults — alpha testers' project data is on screen.
        // Toggle off later if we need pixel-level replays for triage.
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Tracing: 100% in dev, 10% in prod to keep quota usage sane.
    tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.1,

    // Session replay: 10% of sessions, 100% of error sessions.
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // PII discipline (RISK-002 / CV-638). Sentry must NOT auto-attach user IP,
    // email, or cookies; we scrub event payloads before send as a second line
    // of defence (some captured exceptions stringify Request/Response objects).
    sendDefaultPii: false,
    enableLogs: true,

    beforeSend: scrubSentryEvent,
  });
}
